/**
 * Field Rules Engine (Issue #1551 — WS1)
 *
 * Pure-function declarative rule schema for the loan sizer's per-field
 * constraints, smart defaults, and tip buckets. Both backend
 * (33-pricing-engine-service) and frontend (10-business-portal) import the
 * same rules; the backend stays canonical for the persisted FieldRange[],
 * but the frontend can evaluate rules client-side for instant slider
 * feedback before the backend round-trip lands.
 *
 * INVARIANT: This module MUST stay pure. No NestJS, no axios, no Node fs,
 * no React, no Prisma. The .claude/skills/fiops-gap-finder/verifiers/
 * field-rules-purity.sh verifier enforces this — adding a banned import
 * fails the verifier and the gap-finder loop will surface it.
 *
 * Owned by 33-pricing-engine-service. Consumed by:
 * - 33-pricing-engine-service/src/modules/sizer/sizer-calculator.service.ts (canonical)
 * - 10-business-portal/src/components/loans/sizing/SizingFieldRow.tsx (preview)
 * - 10-business-portal/src/components/loans/LoanOverviewPanel.tsx (smart defaults + auto-vs-manual)
 */

import { FieldRangeStatus, SizingFieldName } from './loan-sizer.dto';

// ============================================================================
// CORE TYPES
// ============================================================================

export type RuleSeverity = 'error' | 'warn';

/**
 * Tunable threshold knobs that drive tip-bucket cutoffs and constraint
 * triggers (Issue #1551 — industry-best-practices follow-up).
 *
 * Hardcoded literals were lifted out of every rule into this struct so that
 * a single SizerProfile / business-level config can override them once the
 * admin UI lands. Until then, every consumer MUST pass
 * `DEFAULT_RULE_THRESHOLDS` — defaults match the previous hardcoded values
 * exactly so behaviour is unchanged on this commit.
 *
 * Provenance: most defaults are OpsMatrix internal v1 opinions pending
 * backtest calibration (see `33-pricing-engine-service/scripts/field-rules-backtest.ts`).
 * The hard-error invariants (ARV ≤ purchase, ARV < purchase + rehab) are
 * underwriting fundamentals and don't move. The FICO 7-bucket thresholds
 * mirror the standard consumer-FICO category boundaries.
 */
export interface RuleThresholds {
  rehab: {
    /** Tip-bucket cutoff: rehab/purchase < this → "Light rehab". Default 0.05 */
    lightCutoff: number;
    /** Tip-bucket cutoff: rehab/purchase < this → "Standard". Default 0.15 */
    standardCutoff: number;
    /** Tip-bucket cutoff: rehab/purchase < this → "Heavy". Default 0.30 (>= → "Extreme") */
    heavyCutoff: number;
    /** Constraint: rehab/purchase > this triggers warn. Default 0.50 */
    warnAboveCutoff: number;
    /** Constraint: (purchase + rehab) / arv > this triggers thin-equity warn. Default 0.95 */
    arvMarginCutoff: number;
  };
  arv: {
    /** Tip cutoff: arv/purchase < this → "Conservative lift". Default 1.10 */
    conservativeCutoff: number;
    /** Tip cutoff: arv/purchase < this → "Typical lift". Default 1.25 */
    typicalCutoff: number;
    /** Tip cutoff: arv/purchase < this → "Aggressive lift". Default 1.40 (>= → "Extreme") */
    aggressiveCutoff: number;
  };
  purchase: {
    /** Tip cutoff: purchase < this → "Small deal". Default 500_000 */
    smallDealCutoff: number;
    /** Tip cutoff: purchase < this → "Mid-market". Default 2_000_000 */
    midDealCutoff: number;
    /** Tip cutoff: purchase < this → "Large deal". Default 5_000_000 (>= → "Jumbo") */
    largeDealCutoff: number;
  };
  rate: {
    /** Tip cutoff: rate (percent) < this → "Low end of band". Default 8 */
    lowEndCutoff: number;
    /** Tip cutoff: rate (percent) < this → "Mid-band". Default 12 (>= → "High end") */
    midBandCutoff: number;
  };
  points: {
    /** Tip cutoff: points < this → "Low points". Default 1.5 */
    lowCutoff: number;
    /** Tip cutoff: points < this → "Standard points". Default 3 (>= → "High points") */
    standardCutoff: number;
  };
  term: {
    /** Tip cutoff: termMonths ≤ this → "Short bridge". Default 12 */
    shortBridgeMonths: number;
    /** Tip cutoff: termMonths ≤ this → "Standard bridge". Default 24 (> → "Extended") */
    standardBridgeMonths: number;
  };
  fico: {
    /** Constraint: floor + this distance triggers "barely clears" tip. Default 20 */
    barelyClearsBuffer: number;
    /** Tip cutoff: < this → "Fair credit". Default 670 */
    fairCutoff: number;
    /** Tip cutoff: < this → "Good credit". Default 740 */
    goodCutoff: number;
    /** Tip cutoff: < this → "Very Good credit". Default 800 */
    veryGoodCutoff: number;
    /** Tip cutoff: < this → "Exceptional". Default 830 (>= → "Top of scale") */
    exceptionalCutoff: number;
  };
  loanLeverage: {
    /** Tip: ltc or arltv >= cap × this → "At cap". Default 0.95 */
    atCapWithin: number;
    /** Tip: ltc < cap × this → "Well below caps". Default 0.70 */
    wellBelowCutoff: number;
  };
}

/**
 * Platform-wide defaults for `RuleThresholds`. These match the hardcoded
 * literals that lived inside every rule before Issue #1551's calibration
 * follow-up — overriding the engine with this default produces the same
 * tip-bucket and constraint behaviour as before. Override per-tenant via
 * `SizerProfile.insightsConfig` (admin UI is a separate workstream).
 */
export const DEFAULT_RULE_THRESHOLDS: RuleThresholds = {
  rehab: {
    lightCutoff: 0.05,
    standardCutoff: 0.15,
    heavyCutoff: 0.3,
    warnAboveCutoff: 0.5,
    arvMarginCutoff: 0.95,
  },
  arv: {
    conservativeCutoff: 1.1,
    typicalCutoff: 1.25,
    aggressiveCutoff: 1.4,
  },
  purchase: {
    smallDealCutoff: 500_000,
    midDealCutoff: 2_000_000,
    largeDealCutoff: 5_000_000,
  },
  rate: {
    lowEndCutoff: 8,
    midBandCutoff: 12,
  },
  points: {
    lowCutoff: 1.5,
    standardCutoff: 3,
  },
  term: {
    shortBridgeMonths: 12,
    standardBridgeMonths: 24,
  },
  fico: {
    barelyClearsBuffer: 20,
    fairCutoff: 670,
    goodCutoff: 740,
    veryGoodCutoff: 800,
    exceptionalCutoff: 830,
  },
  loanLeverage: {
    atCapWithin: 0.95,
    wellBelowCutoff: 0.7,
  },
};

/**
 * Sibling-field context evaluated alongside the rule's own value. Sourced
 * from the active sizer policy + current form values. Every numeric input
 * is nullable — null = unknown / not yet set.
 *
 * Callers MUST set `thresholds` — usually to `DEFAULT_RULE_THRESHOLDS`. A
 * future override surface (admin UI / SizerProfile.insightsConfig) will
 * thread custom values through the same field.
 */
export interface RuleContext {
  // Sibling input values (the rule for field X has access to all other Xs)
  purchase: number | null;
  rehabBudget: number | null;
  arv: number | null;
  requestedLoan: number | null;
  rate: number | null;
  points: number | null;
  termMonths: number | null;
  fico: number | null;

  // Tier + cap context from the active sizer profile
  tier: number;
  ficoFloor: number;
  totalLtcCap: number;
  arltvCap: number;
  tierMaxLoan: number | null;
  tierMaxTermMonths: number | null;

  // Loan-type context (e.g. 'fix-flip', 'dscr', 'ground-up')
  loanType: string;

  /**
   * Tunable threshold knobs (tip-bucket cutoffs, warn triggers). Pass
   * `DEFAULT_RULE_THRESHOLDS` for the platform-wide defaults; override per
   * tenant once admin UI lands.
   */
  thresholds: RuleThresholds;
}

export interface RuleConstraint {
  predicate: (ctx: RuleContext, value: number | null) => boolean;
  severity: RuleSeverity;
  message: (ctx: RuleContext, value: number | null) => string;
  /**
   * Provenance — citation for the threshold/rule. Free-text; e.g.
   * `'FNMA Selling Guide B7-1, 2024-08'`,
   * `'OpsMatrix internal v1: defaults pending backtest calibration (issue #1551)'`,
   * or `'Senior LO judgment, Coastal Funding 2026-Q1'`.
   *
   * Required so callers can surface the source of each rule in tooltips,
   * audit trails, and the calibration backtester report.
   */
  cite: string;
}

export interface RuleTipBucket {
  match: (ctx: RuleContext, value: number | null) => boolean;
  message: (ctx: RuleContext, value: number | null) => string;
  /**
   * Provenance — citation for the bucket boundary. Free-text; e.g.
   * `'Standard consumer FICO categories — see myfico.com / Equifax credit score ranges (300-850)'`,
   * `'OpsMatrix internal v1 default (issue #1551) — pending backtest calibration'`,
   * or `'Senior LO judgment, Coastal Funding 2026-Q1'`.
   *
   * Required so callers can surface the source of each bucket in tooltips,
   * audit trails, and the calibration backtester report.
   */
  cite: string;
}

export interface FieldRule {
  field: SizingFieldName;
  /**
   * Smart default given the current sibling values. Returns null when there
   * is not enough sibling context to suggest a value. Callers track the
   * origin (auto vs manual) and only overwrite auto values when siblings
   * change.
   */
  initialValue?: (ctx: RuleContext) => number | null;
  /** Constraints evaluated for severity ordering — error wins over warn. */
  constraints: RuleConstraint[];
  /** First-match-wins. Most specific bucket should be earliest. */
  tips: RuleTipBucket[];
}

export interface RuleEvaluation {
  /** Worst-severity violation (error trumps warn). null = no violations. */
  violation: { severity: RuleSeverity; message: string } | null;
  /** First matching tip. null = no bucket matched. */
  tip: string | null;
  /** User-facing reason — violation if any, else tip, else empty. */
  reason: string;
  /** FieldRangeStatus for the existing FieldRange[] consumer. */
  status: FieldRangeStatus;
}

/**
 * Evaluate a rule against the given context and value. Pure — same inputs
 * always produce the same output. No I/O, no clock reads, no randomness.
 */
export function evaluateField(rule: FieldRule, ctx: RuleContext, value: number | null): RuleEvaluation {
  let violation: RuleEvaluation['violation'] = null;
  for (const c of rule.constraints) {
    if (c.predicate(ctx, value)) {
      const candidate = { severity: c.severity, message: c.message(ctx, value) };
      // Error trumps warn — keep the error if we already saw one
      if (!violation) {
        violation = candidate;
      } else if (violation.severity !== 'error' && candidate.severity === 'error') {
        violation = candidate;
      }
    }
  }

  let tip: string | null = null;
  for (const b of rule.tips) {
    if (b.match(ctx, value)) {
      tip = b.message(ctx, value);
      break;
    }
  }

  const reason = violation ? violation.message : (tip ?? '');
  let status: FieldRangeStatus;
  if (value === null) {
    status = FieldRangeStatus.SUGGESTED;
  } else if (violation) {
    status = violation.severity === 'error' ? FieldRangeStatus.UNDER : FieldRangeStatus.BINDS;
  } else {
    status = FieldRangeStatus.SUPPORTS;
  }

  return { violation, tip, reason, status };
}

// ============================================================================
// PER-FIELD RULES
// ============================================================================

/** FICO scale tops out at 850 by definition; cap UI bands here. */
export const FICO_SCALE_MAX = 850;

// Honest provenance — most thresholds are internal opinions pending
// calibration. Don't fabricate FNMA bulletin numbers; if it's our judgment,
// say so.
const CITE_INTERNAL_V1 = 'OpsMatrix internal v1 default (issue #1551) — pending backtest calibration';
const CITE_INVARIANT_NO_VALUE_CREATED = 'Underwriting invariant — no value created if ARV ≤ cost basis';
const CITE_INVARIANT_COST_EXCEEDS_VALUE =
  'Underwriting invariant — total cost basis (purchase + rehab) cannot exceed finished value';
const CITE_TIER_CAP = 'Active SizerProfile tier configuration (per-tenant policy)';
const CITE_FICO_CONSUMER_BANDS =
  'Standard consumer FICO categories — see myfico.com / Equifax credit score ranges (300-850)';
const CITE_FICO_TOP_OF_SCALE = 'FICO scale upper bound (850) — model definition, not policy';
const CITE_NON_NEGATIVITY = 'Domain invariant — value must be non-negative';
const CITE_MUST_BE_POSITIVE = 'Domain invariant — value must be positive (downstream divide-by-zero guard)';

// === Purchase price ==========================================================
export const purchasePriceRule: FieldRule = {
  field: SizingFieldName.PURCHASE_PRICE,
  initialValue: (_ctx) => null,
  constraints: [
    {
      predicate: (_ctx, v) => v !== null && v <= 0,
      severity: 'error',
      message: () => 'Purchase price must be greater than zero',
      cite: CITE_MUST_BE_POSITIVE,
    },
  ],
  tips: [
    {
      match: (ctx, v) => v !== null && v < ctx.thresholds.purchase.smallDealCutoff,
      message: () => 'Small deal (<$500K) — verify exit market depth',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v < ctx.thresholds.purchase.midDealCutoff,
      message: () => 'Mid-market deal',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v < ctx.thresholds.purchase.largeDealCutoff,
      message: () => 'Large deal — verify lender appetite at this size',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v >= ctx.thresholds.purchase.largeDealCutoff,
      message: () => 'Jumbo deal (>$5M) — likely needs participation or warehouse capacity check',
      cite: CITE_INTERNAL_V1,
    },
  ],
};

// === Rehab budget ============================================================
export const rehabBudgetRule: FieldRule = {
  field: SizingFieldName.REHAB_BUDGET,
  // Conservative default: 10% of purchase. Officer can adjust; flips to manual.
  initialValue: (ctx) => (ctx.purchase !== null && ctx.purchase > 0 ? Math.round(ctx.purchase * 0.1) : null),
  constraints: [
    {
      predicate: (_ctx, v) => v !== null && v < 0,
      severity: 'error',
      message: () => 'Rehab budget cannot be negative',
      cite: CITE_NON_NEGATIVITY,
    },
    {
      predicate: (ctx, v) =>
        v !== null && ctx.purchase !== null && v > ctx.purchase * ctx.thresholds.rehab.warnAboveCutoff,
      severity: 'warn',
      message: () => 'Rehab >50% of purchase — verify scope is realistic',
      cite: CITE_INTERNAL_V1,
    },
    {
      // ARV margin warning: total cost basis within 5% of ARV (default)
      predicate: (ctx, v) =>
        v !== null &&
        ctx.purchase !== null &&
        ctx.arv !== null &&
        ctx.purchase + v > ctx.arv * ctx.thresholds.rehab.arvMarginCutoff,
      severity: 'warn',
      message: () => 'Total cost basis (purchase + rehab) within 5% of ARV — thin equity margin',
      cite: CITE_INTERNAL_V1,
    },
  ],
  tips: [
    {
      match: (ctx, v) =>
        v !== null && ctx.purchase !== null && ctx.purchase > 0 && v / ctx.purchase < ctx.thresholds.rehab.lightCutoff,
      message: () => 'Light rehab (<5% of purchase) — verify scope captures all needed work',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) =>
        v !== null &&
        ctx.purchase !== null &&
        ctx.purchase > 0 &&
        v / ctx.purchase < ctx.thresholds.rehab.standardCutoff,
      message: () => 'Standard rehab budget (5-15% of purchase)',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) =>
        v !== null && ctx.purchase !== null && ctx.purchase > 0 && v / ctx.purchase < ctx.thresholds.rehab.heavyCutoff,
      message: () => 'Heavy rehab (15-30% of purchase) — confirm contractor bids',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) =>
        v !== null && ctx.purchase !== null && ctx.purchase > 0 && v / ctx.purchase >= ctx.thresholds.rehab.heavyCutoff,
      message: () => 'Extreme rehab (>30% of purchase) — verify ARV comp methodology',
      cite: CITE_INTERNAL_V1,
    },
  ],
};

// === ARV (After-repair value) ================================================
export const arvRule: FieldRule = {
  field: SizingFieldName.ARV,
  // Default: max(purchase × 1.15, purchase + rehab × 1.3)
  initialValue: (ctx) => {
    if (ctx.purchase === null || ctx.purchase <= 0) return null;
    const rehab = ctx.rehabBudget ?? 0;
    const lift = Math.max(ctx.purchase * 1.15, ctx.purchase + rehab * 1.3);
    return Math.round(lift);
  },
  constraints: [
    {
      // P0: ARV ≤ purchase = no value created (rehab destroys value)
      predicate: (ctx, v) => v !== null && ctx.purchase !== null && v <= ctx.purchase,
      severity: 'error',
      message: () => 'ARV ≤ purchase — rehab destroys value, no value creation in deal',
      cite: CITE_INVARIANT_NO_VALUE_CREATED,
    },
    {
      // P0: ARV < purchase + rehab = total cost exceeds finished value
      predicate: (ctx, v) =>
        v !== null && ctx.purchase !== null && ctx.rehabBudget !== null && v < ctx.purchase + ctx.rehabBudget,
      severity: 'error',
      message: () => 'ARV < purchase + rehab — total cost exceeds finished value',
      cite: CITE_INVARIANT_COST_EXCEEDS_VALUE,
    },
    {
      // ARLTV-required floor: requested loan / arltvCap > ARV
      predicate: (ctx, v) =>
        v !== null && ctx.requestedLoan !== null && ctx.arltvCap > 0 && v < ctx.requestedLoan / ctx.arltvCap,
      severity: 'warn',
      message: (ctx) => `ARV below ARLTV-required floor for requested loan (${(ctx.arltvCap * 100).toFixed(0)}% cap)`,
      cite: CITE_TIER_CAP,
    },
  ],
  tips: [
    {
      match: (ctx, v) =>
        v !== null &&
        ctx.purchase !== null &&
        ctx.purchase > 0 &&
        v / ctx.purchase < ctx.thresholds.arv.conservativeCutoff,
      message: () => 'Conservative lift (5-10%)',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) =>
        v !== null && ctx.purchase !== null && ctx.purchase > 0 && v / ctx.purchase < ctx.thresholds.arv.typicalCutoff,
      message: () => 'Typical lift (15-25%)',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) =>
        v !== null &&
        ctx.purchase !== null &&
        ctx.purchase > 0 &&
        v / ctx.purchase < ctx.thresholds.arv.aggressiveCutoff,
      message: () => 'Aggressive lift (25-40%) — verify comps',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) =>
        v !== null &&
        ctx.purchase !== null &&
        ctx.purchase > 0 &&
        v / ctx.purchase >= ctx.thresholds.arv.aggressiveCutoff,
      message: () => 'Extreme lift (>40%) — strong comp evidence required',
      cite: CITE_INTERNAL_V1,
    },
  ],
};

// === Requested loan ==========================================================
export const requestedLoanRule: FieldRule = {
  field: SizingFieldName.REQUESTED_LOAN,
  initialValue: (ctx) => {
    if (ctx.purchase === null || ctx.purchase <= 0) return null;
    const ltcMax = ctx.purchase * ctx.totalLtcCap;
    const arltvMax = ctx.arv !== null ? ctx.arv * ctx.arltvCap : Number.POSITIVE_INFINITY;
    const tierMax = ctx.tierMaxLoan ?? Number.POSITIVE_INFINITY;
    const max = Math.min(ltcMax, arltvMax, tierMax);
    return Number.isFinite(max) ? Math.round(max) : null;
  },
  constraints: [
    {
      predicate: (ctx, v) => v !== null && ctx.tierMaxLoan !== null && v > ctx.tierMaxLoan,
      severity: 'error',
      message: (ctx) => `Above tier ${ctx.tier} max loan ($${ctx.tierMaxLoan?.toLocaleString()})`,
      cite: CITE_TIER_CAP,
    },
    {
      predicate: (ctx, v) => v !== null && ctx.purchase !== null && v > ctx.purchase * ctx.totalLtcCap,
      severity: 'error',
      message: (ctx) => `Above Total LTC cap (${(ctx.totalLtcCap * 100).toFixed(0)}%)`,
      cite: CITE_TIER_CAP,
    },
    {
      predicate: (ctx, v) => v !== null && ctx.arv !== null && v > ctx.arv * ctx.arltvCap,
      severity: 'error',
      message: (ctx) => `Above ARLTV cap (${(ctx.arltvCap * 100).toFixed(0)}%)`,
      cite: CITE_TIER_CAP,
    },
  ],
  tips: [
    {
      match: (ctx, v) => {
        if (v === null || ctx.purchase === null || ctx.purchase === 0) return false;
        const ltc = v / ctx.purchase;
        const arltv = ctx.arv !== null && ctx.arv > 0 ? v / ctx.arv : 0;
        const cap = ctx.thresholds.loanLeverage.atCapWithin;
        return ltc >= ctx.totalLtcCap * cap || arltv >= ctx.arltvCap * cap;
      },
      message: () => 'At cap — full leverage utilized',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => {
        if (v === null || ctx.purchase === null || ctx.purchase === 0) return false;
        const ltc = v / ctx.purchase;
        return ltc < ctx.totalLtcCap * ctx.thresholds.loanLeverage.wellBelowCutoff;
      },
      message: () => 'Well below caps — could push leverage',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && ctx.purchase !== null && ctx.purchase > 0,
      message: (ctx, v) => {
        const ltc = (v as number) / (ctx.purchase as number);
        const arltv = ctx.arv !== null && ctx.arv > 0 ? (v as number) / ctx.arv : null;
        return arltv !== null
          ? `${(ltc * 100).toFixed(0)}% of purchase, ${(arltv * 100).toFixed(0)}% of ARV`
          : `${(ltc * 100).toFixed(0)}% of purchase`;
      },
      cite: CITE_INTERNAL_V1,
    },
  ],
};

// === Rate ====================================================================
export const rateRule: FieldRule = {
  field: SizingFieldName.RATE,
  initialValue: () => null, // tier default supplied by caller
  constraints: [],
  tips: [
    {
      match: (ctx, v) => v !== null && v < ctx.thresholds.rate.lowEndCutoff,
      message: () => 'Low end of band',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v < ctx.thresholds.rate.midBandCutoff,
      message: () => 'Mid-band',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v >= ctx.thresholds.rate.midBandCutoff,
      message: () => 'High end — rate-shop or verify tier alignment',
      cite: CITE_INTERNAL_V1,
    },
  ],
};

// === Points ==================================================================
export const pointsRule: FieldRule = {
  field: SizingFieldName.POINTS,
  initialValue: () => null,
  constraints: [
    {
      predicate: (_c, v) => v !== null && v < 0,
      severity: 'error',
      message: () => 'Points cannot be negative',
      cite: CITE_NON_NEGATIVITY,
    },
  ],
  tips: [
    {
      match: (ctx, v) => v !== null && v < ctx.thresholds.points.lowCutoff,
      message: () => 'Low points',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v < ctx.thresholds.points.standardCutoff,
      message: () => 'Standard points',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v >= ctx.thresholds.points.standardCutoff,
      message: () => 'High points — verify total cost vs rate trade-off',
      cite: CITE_INTERNAL_V1,
    },
  ],
};

// === Term (months) ===========================================================
export const termMonthsRule: FieldRule = {
  field: SizingFieldName.TERM_MONTHS,
  initialValue: () => null,
  constraints: [
    {
      predicate: (ctx, v) => v !== null && ctx.tierMaxTermMonths !== null && v > ctx.tierMaxTermMonths,
      severity: 'error',
      message: (ctx) => `Above tier ${ctx.tier} max term (${ctx.tierMaxTermMonths} months)`,
      cite: CITE_TIER_CAP,
    },
  ],
  tips: [
    {
      match: (ctx, v) => v !== null && v <= ctx.thresholds.term.shortBridgeMonths,
      message: () => 'Short bridge (≤12 months)',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v <= ctx.thresholds.term.standardBridgeMonths,
      message: () => 'Standard bridge term',
      cite: CITE_INTERNAL_V1,
    },
    {
      match: (ctx, v) => v !== null && v > ctx.thresholds.term.standardBridgeMonths,
      message: () => 'Extended term — verify exit assumption',
      cite: CITE_INTERNAL_V1,
    },
  ],
};

// === FICO — 7-bucket scheme (refactored from sizer-calculator buildFicoReason) ===
// Buckets evaluated in order — first match wins:
//   - below floor              → "won't qualify"           [constraint, error]
//   - floor … floor+20         → "barely clears"           [tip]
//   - floor+20 … 670           → Fair credit               [tip]
//   - 670 … 740                → Good credit               [tip]
//   - 740 … 800                → Very Good credit          [tip]
//   - 800 … 830                → Exceptional credit        [tip]
//   - 830 … 850                → top of FICO scale         [tip]
//
// The standard consumer-scale "Poor (<580)" bucket is unreachable here —
// anything below the active tier's floor is caught by the "won't qualify"
// constraint first. Only the *message* is constructed here; the band's
// UNDER / SUPPORTS / BINDS classification is computed by `evaluateField`.
export const ficoRule: FieldRule = {
  field: SizingFieldName.FICO,
  initialValue: () => null,
  constraints: [
    {
      predicate: (ctx, v) => v !== null && v < ctx.ficoFloor,
      severity: 'error',
      message: (ctx, v) => {
        const below = ctx.ficoFloor - (v as number);
        return `FICO ${v} is ${below} below tier ${ctx.tier}'s ${ctx.ficoFloor} floor — won't qualify; consider a lower tier or improve credit`;
      },
      cite: CITE_TIER_CAP,
    },
  ],
  tips: [
    {
      // Barely clears — within `barelyClearsBuffer` of floor (default 20)
      match: (ctx, v) => v !== null && v >= ctx.ficoFloor && v < ctx.ficoFloor + ctx.thresholds.fico.barelyClearsBuffer,
      message: (ctx, v) =>
        `FICO ${v} barely clears tier ${ctx.tier}'s ${ctx.ficoFloor} floor — any drop risks disqualification`,
      cite: CITE_INTERNAL_V1,
    },
    {
      // Fair (above floor+barelyClearsBuffer but below `fairCutoff`)
      match: (ctx, v) => v !== null && v < ctx.thresholds.fico.fairCutoff,
      message: (ctx, v) => `FICO ${v} — Fair credit, above tier ${ctx.tier}'s ${ctx.ficoFloor} floor but not strong`,
      cite: CITE_FICO_CONSUMER_BANDS,
    },
    {
      // Good
      match: (ctx, v) => v !== null && v < ctx.thresholds.fico.goodCutoff,
      message: (ctx, v) => `FICO ${v} — Good credit, comfortably above tier ${ctx.tier}'s ${ctx.ficoFloor} floor`,
      cite: CITE_FICO_CONSUMER_BANDS,
    },
    {
      // Very Good
      match: (ctx, v) => v !== null && v < ctx.thresholds.fico.veryGoodCutoff,
      message: (ctx, v) => `FICO ${v} — Very Good credit, solidly above tier ${ctx.tier}'s ${ctx.ficoFloor} floor`,
      cite: CITE_FICO_CONSUMER_BANDS,
    },
    {
      // Exceptional (800-829 by default)
      match: (ctx, v) => v !== null && v < ctx.thresholds.fico.exceptionalCutoff,
      message: (ctx, v) =>
        `FICO ${v} — Exceptional credit (800+), well above tier ${ctx.tier}'s ${ctx.ficoFloor} floor`,
      cite: CITE_FICO_CONSUMER_BANDS,
    },
    {
      // Top of scale (830-850 by default)
      match: (ctx, v) => v !== null && v >= ctx.thresholds.fico.exceptionalCutoff,
      message: (_ctx, v) => `FICO ${v} — top of the FICO scale (${v}/850), clears every tier with margin`,
      cite: CITE_FICO_TOP_OF_SCALE,
    },
    {
      // Null value — show the requirement instead of the bucket. evaluateField
      // already maps null to FieldRangeStatus.SUGGESTED; this tip provides
      // the user-facing copy that explains the active tier's expectation.
      match: (_c, v) => v === null,
      message: (ctx) => `Tier ${ctx.tier} requires FICO ≥ ${ctx.ficoFloor} (FICO scale 300–${FICO_SCALE_MAX})`,
      cite: CITE_TIER_CAP,
    },
  ],
};

// ============================================================================
// REGISTRY
// ============================================================================

export const ALL_FIELD_RULES: readonly FieldRule[] = [
  purchasePriceRule,
  rehabBudgetRule,
  arvRule,
  requestedLoanRule,
  rateRule,
  pointsRule,
  termMonthsRule,
  ficoRule,
] as const;

const fieldRulesByName: Partial<Record<SizingFieldName, FieldRule>> = Object.fromEntries(
  ALL_FIELD_RULES.map((r) => [r.field, r]),
);

export function getRule(field: SizingFieldName): FieldRule | undefined {
  return fieldRulesByName[field];
}

export function getAllRules(): readonly FieldRule[] {
  return ALL_FIELD_RULES;
}
